import React from 'react';
import { useAtom } from 'jotai';
import { npvrNotificationStateAtom, npvrNotificationTestModeAtom } from '@smart-tv/atoms';
import { NpvrNotificationName } from '@smart-tv/typings/npvr';
import getNpvrNotificationState from '@smart-tv/utils/kmm/getNpvrNotificationState';
import setNpvrNotificationState from '@smart-tv/utils/kmm/setNpvrNotificationState';

export const npvrNotificationTabs = {
  errors: 'FAILED',
  expiring: 'EXPIRING_SOON',
  planned: 'SCHEDULED'
} as const satisfies Record<string, NpvrNotificationName>;

export type NpvrNotificationTab = keyof typeof npvrNotificationTabs;

export const useNpvrNotifications = () => {
  const [state, setState] = useAtom(npvrNotificationStateAtom);
  const [testMode, setTestMode] = useAtom(npvrNotificationTestModeAtom);
  const isMountedRef = React.useRef(true);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const refresh = React.useCallback(async () => {
    if (testMode) return false;

    try {
      const nextState = await getNpvrNotificationState();
      if (isMountedRef.current) setState(nextState);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }, [testMode, setState]);

  const markDialogSeen = React.useCallback(
    async (notificationName: 'FAILED' | 'SCHEDULED') => {
      if (!testMode) {
        try {
          const success = await setNpvrNotificationState(notificationName, 'DIALOG');
          if (!success) return;
        } catch (error) {
          console.error(error);
          return;
        }
      }

      if (!isMountedRef.current) return;
      setState(
        (current) =>
          current && {
            ...current,
            ...(notificationName === 'FAILED'
              ? { dialogFailedEventsShown: false }
              : { dialogScheduledEventsShown: false })
          }
      );
    },
    [testMode, setState]
  );

  const reset = React.useCallback(() => {
    setState(undefined);
    setTestMode(false);
  }, [setState, setTestMode]);

  const hasTabNotification = React.useCallback(
    (tab: string) => {
      if (!state) return false;

      switch (tab as NpvrNotificationTab) {
        case 'errors':
          return state.dotFailedEventsShown;
        case 'expiring':
          return state.dotExpiringSoonEventsShown;
        case 'planned':
          return state.dotScheduledEventsShown;
        default:
          return false;
      }
    },
    [state]
  );

  const hasAnyNotification = state?.dotGlobalShown ?? false;

  // Test mode has no backend to recompute the global flag, so mirror that locally.
  const markTabSeenInTestMode = React.useCallback(
    (tab: string) => {
      setState((current) => {
        if (!current) return current;

        const next = {
          ...current,
          ...(tab === 'errors' && { dotFailedEventsShown: false }),
          ...(tab === 'expiring' && { dotExpiringSoonEventsShown: false }),
          ...(tab === 'planned' && { dotScheduledEventsShown: false })
        };

        next.dotGlobalShown =
          next.dotFailedEventsShown ||
          next.dotExpiringSoonEventsShown ||
          next.dotScheduledEventsShown;

        return next;
      });
    },
    [setState]
  );

  const markTabSeen = React.useCallback(
    async (tab: string) => {
      if (!(tab in npvrNotificationTabs) || !hasTabNotification(tab)) return;

      if (testMode) {
        markTabSeenInTestMode(tab);
        return;
      }

      const notificationName = npvrNotificationTabs[tab as NpvrNotificationTab];

      try {
        await setNpvrNotificationState(notificationName, 'DOT');
      } catch (error) {
        console.error(error);
        return;
      }

      if (!isMountedRef.current) return;

      setState(
        (current) =>
          current && {
            ...current,
            ...(tab === 'errors' && { dotFailedEventsShown: false }),
            ...(tab === 'expiring' && { dotExpiringSoonEventsShown: false }),
            ...(tab === 'planned' && { dotScheduledEventsShown: false })
          }
      );
    },
    [testMode, hasTabNotification, markTabSeenInTestMode, setState]
  );

  return {
    refresh,
    hasTabNotification,
    hasAnyNotification,
    markTabSeen,
    markDialogSeen,
    testMode,
    setTestMode,
    reset
  };
};

export default useNpvrNotifications;
